import React, { useCallback, useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Button } from "@mui/material";
import { Spinner, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/redux/store";
import moment from 'moment'
import { formatDateLocal, formatTimeZone } from "src/utils/TimeZoneDateFormat";
import { KTIcon } from "src/marketConnect/helpers";
import { getInventoryTransferListAsync, initiateTransferInventoryAsync } from "src/redux/actions/omsPageActions";
import CreateEditTransferModal from "./CreateUpdateTransfer";
import TableLoadingShell from "src/utils/TableLoadingShell";
import StatusButton from "../orders/StatusButton";
import { toast } from "react-toastify";
import AppDatePickerRange from "../orders/components/AppDatePickerRange";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import FilterDropdown from "src/marketConnect/helpers/components/FilterDropdown";

const InventoryTransferListing: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [paginationData, setPaginationData] = useState({
    page_num: 1,
    page_limit: 10,
    search: "",
    created_at_min: '',
    created_at_max: '',
    sync_status: '',
    sort_order: '',
    sort_by: '',
  });

  const [searchString, setSearchString] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [initiatingRowId, setInitiatingRowId] = useState<number | null>(null);
  const [isInitiationInProgress, setIsInitiationInProgress] = useState(false);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false)
  const [selectedEndDate, setSelectedEndDate] = useState('')
  const [selectedStarDate, setSelectedStartDate] = useState('')
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const statusKey = "sync_status";
  const filterOptions = ["PENDING", "SYNCED", "FAILED"];
  const [hoveredButton, setHoveredButton] = useState<{
    rowId: number | null
    type: 'edit' | 'transfer' | null
  }>({ rowId: null, type: null })


  const { transferList, pagination, loading } = useSelector(
    (state: any) => state.inventoryTransferSlice
  );


  const fetchTransfers = useCallback(() => {
    dispatch(
      getInventoryTransferListAsync({
        page: paginationData.page_num,
        limit: paginationData.page_limit,
        search: paginationData.search,
       sort_by: paginationData.sort_by || "",
       sort_order: paginationData.sort_order || "",
        include: "",
        created_at_min: paginationData.created_at_min || "",
        created_at_max: paginationData.created_at_max || "",
        sync_status: paginationData.sync_status ? paginationData.sync_status.toLowerCase() : "",
      })
    ).unwrap()
      .catch((error) => {
        console.log(error, "transfer err");
        toast.error(error?.data?.message || "Something went wrong while fetching adjustments");
      });
  }, [paginationData]);


  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  useEffect(() => {
    if (!filters[statusKey]) return;
    setPaginationData(prev => ({
      ...prev,
      sync_status: filters[statusKey] || "",
      page_num: 1
    }));
  }, [filters]);


  const handleCreate = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setShowModal(true);
  };

  const handleInitiate = async (row: any) => {
    try {
      setInitiatingRowId(row.id); // loader for this row
      setIsInitiationInProgress(true); // disable all buttons
      const payload = { inventory_defination_id: row.id };
      const res: any = await dispatch(initiateTransferInventoryAsync(payload));
      if (res?.payload?.status === 200) {
        toast.success(res?.payload?.message || "Transfer initiated successfully");
        fetchTransfers();
      } else {
        toast.error(res?.payload?.data?.message || "Failed to initiate transfer");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setInitiatingRowId(null);
      setIsInitiationInProgress(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    setSelectedStartDate('');
    setSelectedEndDate('');
    setPaginationData({
      page_num: 1,
      page_limit: 10,
      search: '',
      created_at_min: '',
      created_at_max: '',
      sync_status: '',
              sort_order: '',
        sort_by: '',
    });
  };


  const columns: TableColumn<any>[] = [
    {
      name: "Transfer Number",
      selector: (row) => row.reference_number || "-",
      sortable: false,
      center: true,
      width: "180px",
    },
    {
      name: "Reason",
      selector: (row) => row.reason || "-",
      // sortable: true,
      minWidth: "150px",
      center: true,
    },
    {
      name: "From Location",
      selector: (row) => row?.source_warehouse_data?.api_name || "-",
      center: true,
      width: "180px",
    },
    {
      name: "To Location",
      selector: (row) => row?.destination_warehouse_data?.api_name || "-",
      center: true,
      width: "180px",
    },
    {
      name: (
        <>
          <span>Status</span>
          <FilterDropdown
            id={`filter-${statusKey}`}
            filters={filters}
            setFilters={setFilters}
            openFilterKey={openFilterKey}
            setOpenFilterKey={setOpenFilterKey}
            hasActiveFilter={!!paginationData.sync_status}
            options={[
              {
                label: "Filter Status",
                key: statusKey,
                values: filterOptions,
              },
            ]}
          />
        </>
      ),
      center: true,
      width: "180px",
      cell: (row) => (
        <StatusButton
          syncStatus={row.sync_status || row.final_status}
          syncMessage={
            ["ERROR", "ERROR_UNLINKED", "ERROR_NO_MAPPING", "SYNCED_WITH_ERROR"].includes(
              row.sync_status || row.final_status
            )
              ? row.api_message
              : ""
          }
        />
      ),
    },
    {
      name: "Description",
      width: "300px",
      center: true,
      cell: (row) => {
        const text = row.description || "-";
        const truncated =
          text.length > 40 ? text.substring(0, 40) + "..." : text;

        return (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{text}</Tooltip>}
          >
            <span
              style={{
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
                maxWidth: "280px",
              }}
            >
              {truncated}
            </span>
          </OverlayTrigger>
        );
      },
    },
    {
      name: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>Created At</span>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
          >
            <span style={{ cursor: "pointer", color: "darkgray" }}>
              <i className="fa fa-info-circle" />
            </span>
          </OverlayTrigger>
        </div>
      ),
      center: true,
      width: "180px",
      cell: (row) => (row?.created_at ? formatDateLocal(row.created_at) : ""),
    },
    {
      name: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>Updated At</span>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
          >
            <span style={{ cursor: "pointer", color: "darkgray" }}>
              <i className="fa fa-info-circle" />
            </span>
          </OverlayTrigger>
        </div>
      ),
      center: true,
      width: "180px",
      cell: (row) => (row?.updated_at ? formatDateLocal(row.updated_at) : "-"),
    },
    {
      name: "Actions",
      width: "200px",
      cell: (row) => {
        const isThisRowLoading = initiatingRowId === row.id;
        const disableAllButtons = isInitiationInProgress;

        return (
          <div className="d-flex align-items-center gap-2">
            {/* Edit / Eye Button */}
            <button
              className="btn btn-icon btn-bg-light btn-color-primary btn-sm"
              onClick={() => handleEdit(row)}
              disabled={disableAllButtons}
              style={{
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
                border:
                  hoveredButton.rowId === row.id && hoveredButton.type === 'edit'
                    ? '1px solid #0d6efd'
                    : '1px solid transparent',
                transition: 'all 0.2s ease-in-out',
                cursor: disableAllButtons ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={() =>
                setHoveredButton({ rowId: row.id, type: 'edit' })
              }
              onMouseLeave={() =>
                setHoveredButton({ rowId: null, type: null })
              }
            >
              {row?.sync_status === 'SYNCED' ? (
                <KTIcon iconName="eye" className="fs-4" />
              ) : (
                <KTIcon iconName="pencil" className="fs-4" />
              )}
            </button>

            {/* Initiate Transfer Button */}
            <OverlayTrigger placement="top" overlay={<Tooltip>Initiate Transfer</Tooltip>}>
              <button
                className="btn btn-icon btn-bg-light btn-color-primary btn-sm"
                onClick={() => handleInitiate(row)}
                disabled={disableAllButtons || row?.sync_status === 'SYNCED'}
                style={{
                  justifyContent: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  border:
                    hoveredButton.rowId === row.id && hoveredButton.type === 'transfer'
                      ? '1px solid #0d6efd'
                      : '1px solid transparent',
                  transition: 'all 0.2s ease-in-out',
                  cursor: disableAllButtons ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={() =>
                  setHoveredButton({ rowId: row.id, type: 'transfer' })
                }
                onMouseLeave={() =>
                  setHoveredButton({ rowId: null, type: null })
                }
              >
                {isThisRowLoading ? (
                  <Spinner animation="border" size="sm" role="status" />
                ) : (
                  <KTIcon iconName="truck" className="fs-3" />
                )}
              </button>
            </OverlayTrigger>
          </div>

        );
      },
      ignoreRowClick: true,
      button: true,
    }
  ];

  return (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <span className="fw-bold text-lg" style={{ fontSize: "16px" }}>
          Inventory Transfer
        </span>
      </div>

      <div className="card card-flush">
        <div className="card-header d-flex justify-content-between align-items-center">
          {/* LEFT: Search */}
          <div className="position-relative">
            {searchString === "" && (
              <i className="bi bi-arrow-return-left position-absolute top-50 translate-middle-y end-0 me-2 text-secondary"></i>
            )}
            <input
              className="form-control"
              type="search"
              placeholder="Search"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPaginationData((prev) => ({
                    ...prev,
                    search: searchString,
                    page_num: 1,
                  }));
                }
              }}
            />
          </div>

          {/* RIGHT SIDE GROUPED BUTTONS */}
          <div className="d-flex align-items-center gap-2">
            <div className="mx-md-1 my-1 my-md-0">
              <button
                className="btn btn-outline-primary btn-sm date-btn"
                onClick={() => setShowDateRangePicker(!showDateRangePicker)}
              >
                <FontAwesomeIcon icon={faCalendar} className="me-2" />

                {selectedStarDate && selectedEndDate
                  ? `${moment(selectedStarDate).format('YYYY/MM/DD')} - ${moment(
                    selectedEndDate
                  ).format('YYYY/MM/DD')}`
                  : "Pick a Date Range"}
              </button>

              <AppDatePickerRange
                isVisible={showDateRangePicker}
                handleClose={() => setShowDateRangePicker(false)}
                onPickRange={({ startDate, endDate }) => {
                  setSelectedStartDate(startDate);
                  setSelectedEndDate(endDate);
                  setPaginationData((prev) => ({
                    ...prev,
                    created_at_min: moment(startDate).format("YYYY-MM-DD"),
                    created_at_max: moment(endDate).format("YYYY-MM-DD"),
                    page_num: 1,
                  }));
                }}
              />
            </div>
            <div></div> 

            <div>
              <button
                className="btn btn-sm btn-primary w-100px fw-bold py-2"
                onClick={handleResetFilters}
              >
                Reset Filters
              </button>
            </div>

            {/* <Button
              variant="contained"
              className="btn btn-sm btn-primary fw-bold py-2"
              onClick={handleCreate}
            >
              + Add Transfer
            </Button> */}
            <button
              className="btn btn-sm btn-primary fw-bold py-2"
              onClick={handleCreate}
            >
              + Create New Transfer
            </button>
          </div>
        </div>

        <TableLoadingShell loading={loading}>
        <DataTable
          fixedHeader
          persistTableHead
          data={transferList}
          columns={columns}
          pagination
          paginationServer
          paginationTotalRows={pagination.count}
          paginationPerPage={paginationData.page_limit}
          onChangePage={(page) =>
            setPaginationData((prev) => ({ ...prev, page_num: page }))
          }
          onChangeRowsPerPage={(limit) =>
            setPaginationData((prev) => ({
              ...prev,
              page_limit: limit,
              page_num: 1,
            }))
          }
          progressPending={false}
          noDataComponent={<div>No records found</div>}
          customStyles={{
            table: { style: { height: "55vh" } },
            headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
            cells: {
              style: {
                padding: "10px",
                borderBottom: "1px solid gainsboro",
              },
            },
          }}
        />
        </TableLoadingShell>
      </div>

      <CreateEditTransferModal
        show={showModal}
        editData={editData}
        onClose={() => setShowModal(false)}
        onSaved={() => {
          fetchTransfers();
          setShowModal(false);
          setEditData(null);
        }}
      />
    </>
  );
};

export default InventoryTransferListing;
